<template>
    <div
        v-click-outside="close"
        class="quick-links"
        :class="{
            [`theme-${layout.theme}`]: true,
            'open': isOpen || layout.theme === 'clear',
        }"
    >
        <button
            class="ds-btn label"
            :aria-expanded="isOpen || layout.theme === 'clear'"
            @click="isOpen = isOpen ? false : true"
        >
            <span>{{ activeLink?.text }}</span>
            <DSIcon aria-hidden="true" code="chevron-down" size="xs" />
        </button>
        <div
            class="options"
            :inert="!isOpen && layout.theme !== 'clear'"
        >
            <nuxt-link
                v-for="(link, key) in computedLinks"
                :key="`${key}-${link.id}`"
                :to="`/${link.url}`"
                :testtargetid="link.testTargetId"
                class="option"
            >
                {{ link.text }}
            </nuxt-link>
        </div>
    </div>
</template>
<script setup>
import { ref, inject, computed, watch, useRoute } from '#imports';
import vClickOutside from '~dsc/directives/click-outside';

const layout = inject('layout');
const isOpen = ref(false);
const route = useRoute();

const props = defineProps({
    links: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const activeLink = computed(() => props.links.find(link => route.path.includes(`/${link.url}`)) ?? props.links?.[0]);
const computedLinks = computed(() => props.links.filter(link => !link.url.includes(activeLink.value.url)));

const close = () => {
    isOpen.value = false;
};

watch(() => route.path, () => {
    isOpen.value = false;
});
</script>
<style lang="scss" scoped>
@import '../../assets/scss/mixins';

.quick-links {
    position: relative;

    &.theme-black {
        .label {
            color: var(--color-base-white);
        }

        .option {
            color: var(--color-base-white);

            &:hover {
                color: var(--color-base-black);
            }
        }
    }

    &.theme-white {
        .label {
            background: var(--color-base-grey-100);
            border: 2px solid transparent;
            height: 2.5rem;

            .icon {
                color: var(--color-base-black);
                opacity: 1;
            }
        }

        .options {
            padding-top: 1rem;
            border-radius: 1.25rem;
            background: var(--color-base-white);
            box-shadow: var(--panel-box-shadow);
        }

        .option {
            color: var(--color-base-black);
            margin-bottom: .5rem;
        }
    }

    &.theme-clear {
        .label {
            color: var(--color-base-white);
            background: rgb(from var(--color-base-white) r g b / .25);

            .icon {
                @include default-transition();
                width: 0;
                overflow-x: hidden;
                margin: 0;
                opacity: 0;
            }
        }

        .label:hover {
            background: rgb(255, 255, 255, .25);
        }

        .options {
            pointer-events: auto;
            top: 1rem;
            padding: 0;
            background: transparent;
            justify-content: center;
        }

        .option {
            @include default-transition();
            padding: .475rem 1rem;
            border-radius: 1.5rem;
            text-align: center;
            font-size: 1rem;
            color: var(--color-base-white);
            border: 2px solid transparent;

            &:hover {
                background: rgb(255, 255, 255, .25);
            }
        }
    }

    .label {
        display: inline-flex;
        align-items: center;
        min-height: 1.5rem;
        min-width: 35px;
        justify-content: flex-end;
        white-space: nowrap;

        .icon {
            @include default-transition();
            position: relative;
            transform: rotate(0);
            transform-origin: center center;
        }

        &:focus-visible {
            outline: var(--color-base-black) auto 1px;
            outline-color: -webkit-focus-ring-color;
        }
    }

    .options {
        @include default-transition();
        position: absolute;
        right: 50%;
        display: flex;
        align-items: flex-start;
        flex-flow: row wrap;
        transform: translateX(50%) translateY(0) scaleY(0);
        transform-origin: center top;
        border-radius: .25rem;
        padding: .5rem 1rem;
        z-index: 1;

        .option {
            @include default-transition();
            line-height: 1.5em;
            margin-bottom: .5rem;
            white-space: nowrap;
            padding-block: .375rem;

            &:focus-visible {
                outline: var(--color-base-black) auto 1px;
                outline-color: -webkit-focus-ring-color;
            }
        }
    }

    &.open {
        .label {
            .icon {
                transform: rotate(-179.9deg);
            }
        }

        .options {
            @include default-transition();
            transform: translateX(50%) translateY(2rem) scaleY(1);
        }
    }

    @include respond-to('at most small desktop') {
        .label {
            display: none;
        }

        .options {
            position: static;
            opacity: 1;
            transform: scale(1);
            background: none;
            padding: 0;
            box-shadow: none;

            .option {
                &:not(:last-child) {
                    margin-right: 1rem;
                }
            }
        }
    }
}
</style>
